import React, {useCallback} from 'react';

import ColorPicker from '../../../../components/ColorPicker';
import {Analytics} from '../../../../services/analytics';
import {BucketCustomization} from '../types';
import {NumberRangeInput} from './NumberRangeInput';
import {DEFAULT_COLOR} from './utils/getDefaultBucketColors';

const colorPickerStyle = {marginRight: '6px', marginBottom: '8px'};

export const BucketCustomizationInputWithCategoricalConfig = ({
  bucket,
  bucketCustomization,
  index,
  numberOfBuckets,
  updateBucketSetting,
}: {
  updateBucketSetting: (settings: Partial<BucketCustomization>) => void;
  bucket: BucketCustomization;
  bucketCustomization: BucketCustomization;
  index: number;
  numberOfBuckets: number;
}) => {
  const bucketColor =
    bucketCustomization.color ?? bucket.color ?? DEFAULT_COLOR;

  const onUpdateColor = useCallback(
    (color: string) => {
      Analytics.track('Semantic legend bucket color changed', {
        newValue: color,
        index,
      });
      updateBucketSetting({
        color: color,
      });
    },
    [updateBucketSetting, index]
  );

  const onUpdateBucketMax = useCallback(
    (bucketMax: number) => {
      Analytics.track('Semantic legend bucket max changed', {
        newValue: bucketMax,
        index,
      });
      updateBucketSetting({
        bucketMax: bucketMax,
      });
    },
    [updateBucketSetting, index]
  );

  const getMinValue = (
    bucketMin: number,
    index: number,
    bucketCustomization: BucketCustomization
  ) => {
    // if we are at the first bucket, we don't want to show the min value
    return index === 0
      ? undefined
      : (bucketCustomization?.bucketMin ?? bucketMin);
  };

  const getMaxValue = (
    bucketMax: number,
    index: number,
    bucketCustomization: BucketCustomization
  ) => {
    // if we are at the last bucket, we don't want to show the max value
    return index === numberOfBuckets - 1
      ? undefined
      : (bucketCustomization?.bucketMax ?? bucketMax);
  };

  return (
    <div className="flex w-full gap-8" data-test="bucket-customization">
      <NumberRangeInput
        minField="bucketMin"
        maxField="bucketMax"
        minValue={getMinValue(
          bucket.bucketMin ?? 0,
          index,
          bucketCustomization
        )}
        maxValue={getMaxValue(
          bucket.bucketMax ?? 0,
          index,
          bucketCustomization
        )}
        updateSetting={settings => {
          onUpdateBucketMax(settings.bucketMax ?? 0);
        }}
        minDisabled={true} // we never want to allow the min value to be edited
        maxDisabled={true} // TODO: allow max value to be edited
      />
      <ColorPicker
        activeColor={bucketColor ?? ''}
        style={colorPickerStyle}
        setColor={onUpdateColor}
        trigger="button"
      />
    </div>
  );
};
